import React, { useState, useEffect } from 'react';
import { notesAPI } from '../utils/notesAPI';
import { encryptNote } from '../utils/noteCrypto';
import { formatFileSize } from '../utils/noteCrypto';
import './NoteEditor.css';

const NoteEditor = ({ 
  noteId, 
  userKeys, 
  serverPublicKey, 
  onSave, 
  onCancel,
  initialTitle = '',
  initialContent = '',
  isEditing = false 
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(initialContent);
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [lastSaved, setLastSaved] = useState(null);
  const [currentLine, setCurrentLine] = useState(1);
  const [isTyping, setIsTyping] = useState(false);
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [autoSaveMessage, setAutoSaveMessage] = useState('');

  // 监听props变化，更新状态
  useEffect(() => {
    setTitle(initialTitle);
    setContent(initialContent);
    setSaveError('');
    setLastSaved(null);
    setCurrentLine(1);
    setHasUnsavedChanges(false);
    setAutoSaveMessage('');
    // Clear auto-save timer when note changes
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
      setAutoSaveTimer(null);
    }
  }, [noteId, initialTitle, initialContent]);

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
  }, [autoSaveTimer]);

  // 当noteId变化时，重新获取笔记内容
  useEffect(() => {
    if (noteId && userKeys) {
      loadNoteContent();
    }
  }, [noteId, userKeys]);

  // 加载笔记内容
  const loadNoteContent = async () => {
    if (!noteId || !userKeys) return;
    
    try {
      const response = await notesAPI.getNote(noteId, userKeys);
      if (response.success && response.data) {
        const note = response.data;
        // 解密笔记内容
        const { decryptNote } = await import('../utils/noteCrypto');
        const { title, content } = decryptNote(
          note.title_encrypted,
          note.content_encrypted,
          note.note_key_encrypted,
          userKeys.privateKey
        );
        
        setTitle(title);
        setContent(content);
        setSaveError('');
        setLastSaved(null);
        setCurrentLine(1);
      }
    } catch (error) {
      setSaveError('Failed to load note content');
    }
  };

  // Handle title change
  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    setSaveError('');
    setHasUnsavedChanges(true);
    startAutoSaveTimer();
  };

  // Handle content change
  const handleContentChange = (e) => {
    setContent(e.target.value);
    setSaveError('');
    setHasUnsavedChanges(true);
    startAutoSaveTimer();
    
    // Trigger typing animation
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
    }, 500);
  };

  // Calculate current line number based on cursor position
  const getCurrentLine = (textarea) => {
    const textBeforeCursor = textarea.value.substring(0, textarea.selectionStart);
    return textBeforeCursor.split('\n').length;
  };

  // Handle cursor position change
  const handleCursorMove = (e) => {
    const lineNumber = getCurrentLine(e.target);
    setCurrentLine(lineNumber);
  };

  // Handle scroll synchronization
  const handleScroll = (e) => {
    const lineNumbers = document.querySelector('.line-numbers');
    if (lineNumbers) {
      lineNumbers.scrollTop = e.target.scrollTop;
    }
  };

  // Start auto-save timer
  const startAutoSaveTimer = () => {
    // Clear existing timer
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }

    // Set new timer for 10 seconds
    const timer = setTimeout(() => {
      if (hasUnsavedChanges && (title.trim() || content.trim())) {
        performAutoSave();
      }
    }, 10000);

    setAutoSaveTimer(timer);
  };

  // Perform auto-save
  const performAutoSave = async () => {
    if (!userKeys || isLoading) return;

    try {
      // Encrypt content
      const { titleEncrypted, contentEncrypted, noteKeyEncrypted } = encryptNote(
        title, 
        content, 
        userKeys.publicKey
      );

      let response;
      if (isEditing && noteId) {
        // Update existing note
        response = await notesAPI.updateNote(noteId, titleEncrypted, contentEncrypted, noteKeyEncrypted, userKeys);
      } else {
        // Create new note
        response = await notesAPI.createNote(titleEncrypted, contentEncrypted, noteKeyEncrypted, userKeys);
      }

      if (response.success) {
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
        setAutoSaveMessage('Auto-saved successfully');
        // Clear the message after 3 seconds
        setTimeout(() => {
          setAutoSaveMessage('');
        }, 3000);
        // Don't call onSave for auto-save to avoid closing the editor
      }
    } catch (error) {
      // Don't show error for auto-save, just log it
      console.warn('Auto-save failed:', error.message);
    }
  };

  // Manual save button
  const handleManualSave = async () => {
    if (!title.trim() && !content.trim()) {
      setSaveError('Title and content cannot both be empty');
      return;
    }

    setIsLoading(true);
    setSaveError('');

    try {
      // Encrypt content
      const { titleEncrypted, contentEncrypted, noteKeyEncrypted } = encryptNote(
        title, 
        content, 
        userKeys.publicKey
      );

      let response;
      if (isEditing && noteId) {
        // Update existing note
        response = await notesAPI.updateNote(noteId, titleEncrypted, contentEncrypted, noteKeyEncrypted, userKeys);
      } else {
        // Create new note
        response = await notesAPI.createNote(titleEncrypted, contentEncrypted, noteKeyEncrypted, userKeys);
      }

      if (response.success) {
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
        setAutoSaveMessage('');
        // Clear auto-save timer since we just saved manually
        if (autoSaveTimer) {
          clearTimeout(autoSaveTimer);
          setAutoSaveTimer(null);
        }
        if (onSave) {
          onSave(response.data);
        }
      } else {
        throw new Error(response.error || 'Save failed');
      }
    } catch (error) {
      setSaveError(error.message || 'Save failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel editing
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  // Get save status display text
  const getSaveStatusText = () => {
    if (lastSaved) {
      return `Last saved: ${lastSaved.toLocaleTimeString()}`;
    }
    return '';
  };

  // Calculate content size
  const contentSize = new Blob([title + content]).size;

  return (
    <div className="note-editor">
      <div className="editor-header">
        <input
          type="text"
          value={title}
          onChange={handleTitleChange}
          placeholder="Note title..."
          className="title-input"
          maxLength={200}
        />
        
        <div className="editor-actions">
          <div className="save-info">
            {getSaveStatusText() && (
              <div className="last-save">
                {getSaveStatusText()}
              </div>
            )}
            
            <div className="content-size">
              Size: {formatFileSize(contentSize)}
            </div>
          </div>
          
          <div className="action-buttons">
            <button 
              onClick={handleCancel}
              className="cancel-btn"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button 
              onClick={handleManualSave}
              className="save-btn"
              disabled={isLoading || (!title.trim() && !content.trim())}
            >
              {isLoading ? 'Saving...' : (isEditing ? 'Update' : 'Save')}
            </button>
          </div>
        </div>
      </div>

      {saveError && (
        <div className="error-message">
          {saveError}
        </div>
      )}

      <div className="editor-content">
        <div className="editor-with-lines">
          <div className="line-numbers">
            {content.split('\n').map((lineContent, index) => (
              <div key={index} className="line-number">
                {currentLine === index + 1 && (
                  <>
                    <span className="terminal-prompt">:)</span>
                    <span className="terminal-cursor">_</span>
                  </>
                )}
                <span className="line-number-text">{index + 1}</span>
              </div>
            ))}
          </div>
          <div className="content-display">
            {content.split('\n').map((lineContent, index) => (
              <div key={index} className={`content-line ${currentLine === index + 1 ? 'current-line' : ''}`}>
                {lineContent.split('').map((char, charIndex) => (
                  <span 
                    key={charIndex} 
                    className={`content-char ${currentLine === index + 1 && isTyping ? 'typing-bounce' : ''}`}
                    style={{ animationDelay: `${charIndex * 0.05}s` }}
                  >
                    {char}
                  </span>
                ))}
                {lineContent === '' && currentLine === index + 1 && (
                  <span className="content-char typing-bounce" style={{ animationDelay: '0s' }}>
                    &nbsp;
                  </span>
                )}
              </div>
            ))}
          </div>
          <textarea
            value={content}
            onChange={handleContentChange}
            onScroll={handleScroll}
            onKeyUp={handleCursorMove}
            onMouseUp={handleCursorMove}
            onFocus={handleCursorMove}
            placeholder="Start writing your note..."
            className="content-textarea"
            rows={20}
          />
        </div>
      </div>

      <div className="editor-footer">
        <div className="editor-tips">
          <span>
            The system uses 0 log storage. Please do not adjust the window when editing, otherwise the content will be lost. 
            {autoSaveMessage ? (
              <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>{autoSaveMessage}</span>
            ) : hasUnsavedChanges ? (
              ' Auto-save in progress...'
            ) : (
              ' All changes saved.'
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default NoteEditor;